import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from './Modal';
import Icon from './Icon';
import { getAccessToken, isSecretaryContextUser } from '../authStorage';
import { fetchBranches, fetchBranchById, deleteBranch, updateBranch, mapFormToUpdatePayload } from '../branchesApi';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import './BranchesPage.css';
import './DecisionsPage.css';

const PAGE_SIZE = 5;
const MAIN_BRANCH_KEY = 'mithaq_main_branch_id';

function getStoredMainId() {
  try { return Number(localStorage.getItem(MAIN_BRANCH_KEY)) || null; }
  catch { return null; }
}
function storeMainId(id) {
  try { localStorage.setItem(MAIN_BRANCH_KEY, String(id)); } catch {}
}

function BranchesPage() {
  const { direction } = useLanguage();
  const t = useT();
  const navigate = useNavigate();
  const [data, setData]           = useState([]);
  const [search, setSearch]       = useState('');
  const [filterStatus, setFilter] = useState('all');
  const [currentPage, setPage]    = useState(1);
  const [loadingList, setLoadingList] = useState(true);
  const [loadError, setLoadError]     = useState('');
  const [accessDenied, setAccessDenied] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // mainBranchId is stored separately — never depends on the API returning isMain
  const [mainBranchId, setMainBranchId] = useState(() => getStoredMainId());

  useEffect(() => {
    if (!getAccessToken()) { navigate('/login', { replace: true }); return; }
    if (!isSecretaryContextUser()) { setAccessDenied(true); setLoadingList(false); return; }
    let cancelled = false;
    (async () => {
      setLoadError(''); setLoadingList(true);
      try {
        const rows = await fetchBranches();
        if (!cancelled) {
          setData(rows);
          // Prefer localStorage (user's last choice), fall back to API isMain field
          const stored = getStoredMainId();
          if (stored) {
            setMainBranchId(stored);
          } else {
            const mainRow = rows.find((r) => r.isMain);
            if (mainRow) {
              storeMainId(mainRow.id);
              setMainBranchId(mainRow.id);
            }
          }
        }
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : t('branches.loadFailed'));
      } finally {
        if (!cancelled) setLoadingList(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => data.filter((c) => {
    const matchSearch = !search || c.name.includes(search) || c.crn.includes(search) || c.address.includes(search);
    const matchStatus = filterStatus === 'all' || c.status === filterStatus;
    return matchSearch && matchStatus;
  }), [data, search, filterStatus]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated  = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const setAsMain = (item) => {
    const id = item.id;
    storeMainId(id);
    setMainBranchId(id);
    (async () => {
      try {
        const full = await fetchBranchById(id);
        await updateBranch(id, mapFormToUpdatePayload({ ...full, isMain: true }));
      } catch { /* backend may not support isMain */ }
    })();
  };

  const openDelete = (item) => { setDeleteTarget(item); setConfirmOpen(true); };
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteBranch(deleteTarget.id);
      setData((prev) => prev.map((c) => (c.id === deleteTarget.id ? { ...c, status: 'inactive' } : c)));
      setConfirmOpen(false); setDeleteTarget(null); setPage(1);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : t('branches.deleteFailed'));
      setConfirmOpen(false); setDeleteTarget(null);
    }
  };

  const activeCount = data.filter((c) => c.status === 'active').length;

  if (accessDenied) {
    return (
      <div className="page" dir={direction}>
        <div className="page__topbar">
          <div className="page__topbar-left">
            <span className="page__topbar-title">{t('branches.title')}</span>
            <span className="page__topbar-sub">{t('branches.ceaOnly')}</span>
          </div>
        </div>
        <p style={{ padding: 24, color: '#78909c', fontFamily: 'Cairo, sans-serif' }}>{t('common.noPerm')}</p>
      </div>
    );
  }

  return (
    <div className="page" dir={direction}>
      <div className="list-page-card list-page-card--unified">
        <header className="list-page-card__header">
          <div className="list-page-card__titles">
            <span className="list-page-card__title"><Icon name="branch" size={18} color="#00334a" style={{ verticalAlign: 'middle', marginLeft: 6 }} />{t('branches.title')}</span>
            <span className="list-page-card__sub">{t('branches.subtitle')}</span>
          </div>
          <div className="list-page-card__actions">
            <button type="button" className="btn btn--primary" onClick={() => navigate('/app/branches/new')} disabled={loadingList}>
              <Icon name="plus" size={14} color="#fff" /> {t('branches.add')}
            </button>
          </div>
        </header>

        {loadError && <div className="list-page-card__banner list-page-card__banner--error">{loadError}</div>}
        {loadingList && <div className="list-page-card__loading">{t('branches.loading')}</div>}

      {!loadingList && (
      <>
      <div className="page__stats">
        <div className="page__stat-chip page__stat-chip--total"><Icon name="branch" size={13} /> {t('common.total')}: {data.length}</div>
        <div className="page__stat-chip page__stat-chip--active"><Icon name="check-circle" size={13} /> {t('common.active')}: {activeCount}</div>
        <div className="page__stat-chip page__stat-chip--new"><Icon name="clock" size={13} /> {t('common.inactive')}: {data.length - activeCount}</div>
      </div>

      <div className="page__filters">
        <div className="page__search">
          <Icon name="search" size={14} color="#90a4ae" className="page__search-icon" />
          <input className="page__search-input" placeholder={t('branches.search')} value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
          {search && <span style={{ cursor: 'pointer', color: '#90a4ae', fontSize: '0.8rem' }} onClick={() => setSearch('')}>✕</span>}
        </div>
        <select className="page__filter-select" value={filterStatus} onChange={(e) => { setFilter(e.target.value); setPage(1); }}>
          <option value="all">{t('branches.allBranches')}</option>
          <option value="active">{t('branches.activeOnly')}</option>
          <option value="inactive">{t('branches.inactiveOnly')}</option>
        </select>
      </div>

      <div className="table-card">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 42 }}>#</th>
              <th>{t('branches.colBranch')}</th>
              <th>{t('branches.colType')}</th>
              <th>{t('branches.colCrn')}</th>
              <th>{t('common.status')}</th>
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr><td colSpan={6}>
                <div className="empty-state">
                  <div className="empty-state__icon"><Icon name="branch" size={48} color="#cfd8dc" strokeWidth={1.2} /></div>
                  <div className="empty-state__title">{t('branches.noMatch')}</div>
                  <div className="empty-state__sub">{t('branches.noMatchSub')}</div>
                  <button type="button" className="btn btn--primary" style={{ marginTop: 12 }} onClick={() => navigate('/app/branches/new')}>
                    <Icon name="plus" size={14} color="#fff" /> {t('branches.addNew')}
                  </button>
                </div>
              </td></tr>
            ) : paginated.map((item, idx) => (
              <tr key={item.id}>
                <td className="td-num">{(currentPage - 1) * PAGE_SIZE + idx + 1}</td>
                <td>
                  <div className="branch-name-cell">
                    <div className="branch-avatar">{(item.name && item.name.charAt(0)) || '—'}</div>
                    <strong>{item.name}</strong>
                  </div>
                </td>
                <td>
                  {String(item.id) === String(mainBranchId) ? (
                    <span className="branch-type-badge branch-type-badge--main">{t('branches.typeMain')}</span>
                  ) : (
                    <button
                      type="button"
                      className="branch-type-badge branch-type-badge--sub"
                      onClick={() => setAsMain(item)}
                      title={t('branches.setAsMain')}
                    >
                      {t('branches.typeSub')}
                    </button>
                  )}
                </td>
                <td>{item.crn}</td>
                <td>
                  <span className={`status-badge ${item.status === 'active' ? 'status-badge--approved' : 'status-badge--rejected'}`}>
                    {item.status === 'active' ? t('branches.statusActive') : t('branches.statusInactive')}
                  </span>
                </td>
                <td>
                  <div className="td-actions">
                    <button className="action-btn action-btn--view" onClick={() => navigate(`/app/branches/${item.id}`)}><Icon name="eye" size={14} /></button>
                    <button className="action-btn action-btn--edit" onClick={() => navigate(`/app/branches/${item.id}/edit`)}><Icon name="edit" size={14} /></button>
                    <button className="action-btn action-btn--delete" onClick={() => openDelete(item)}><Icon name="trash" size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filtered.length > PAGE_SIZE && (
          <div className="pagination">
            <span>{t('common.showing')} {Math.min(filtered.length, (currentPage - 1) * PAGE_SIZE + 1)}–{Math.min(filtered.length, currentPage * PAGE_SIZE)} {t('common.of')} {filtered.length}</span>
            <div className="pagination__pages">
              <button className="pagination__btn" onClick={() => setPage((p) => p - 1)} disabled={currentPage === 1}>‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button key={p} className={`pagination__btn${currentPage === p ? ' pagination__btn--active' : ''}`} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button className="pagination__btn" onClick={() => setPage((p) => p + 1)} disabled={currentPage === totalPages}>›</button>
            </div>
          </div>
        )}
      </div>
      </>
      )}
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
        itemName={deleteTarget?.name}
        message={t('branches.deactivateMsg')}
      />
    </div>
  );
}

export default BranchesPage;
